import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ErrorAlert } from '@/components/common/ErrorAlert';
import { SettingsByCategory } from '../components/SettingsByCategory';
import { useSettings } from '../hooks/useSettings';
import { usePageTitle } from '@/hooks/usePageTitle';
import type { SettingDto } from '../types/settings';

/**
 * Admin page for managing application-wide settings
 */
export function SettingsPage() {
  usePageTitle('System Settings');
  const { data: settings, isLoading, error, setData } = useSettings();

  const handleSettingUpdated = (updatedSetting: SettingDto) => {
    // Update the setting within its category group
    setData((prevSettings) =>
      prevSettings.map((categoryGroup) => ({
        ...categoryGroup,
        settings: categoryGroup.settings.map((s) =>
          s.key === updatedSetting.key ? updatedSetting : s
        ),
      }))
    );
  };

  return (
    <div className="min-h-screen bg-background px-5 py-6">
      <div className="container mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-secondary">System Settings</h1>
          <p className="text-muted-foreground mt-1">
            Configure application-wide settings
          </p>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="flex justify-center py-12">
            <LoadingSpinner size="lg" label="Loading settings..." />
          </div>
        )}

        {/* Error State */}
        {error && !isLoading && (
          <ErrorAlert error={error} className="mb-4" />
        )}

        {/* Settings Content */}
        {!isLoading && !error && (
          <SettingsByCategory
            settings={settings}
            onSettingUpdated={handleSettingUpdated}
          />
        )}
      </div>
    </div>
  );
}
